import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBlog } from '../context/BlogContext';
import PageTransition from '../components/PageTransition';
import RichEditor from '../components/RichEditor';

export default function EditPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { posts, loading, updatePost, removePostImage } = useBlog();
  const post = posts.find((p) => p.id === id);

  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [readTime, setReadTime] = useState('');
  const [newImageFile, setNewImageFile] = useState(null);
  const [currentImage, setCurrentImage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (post) {
      setTitle(post.title || '');
      setExcerpt(post.excerpt || '');
      setContent(post.content || '');
      setTags((post.tags || []).join(', '));
      setReadTime(post.readTime ? String(post.readTime) : '');
      setCurrentImage(post.image || '');
    }
  }, [post]);

  if (loading) return <div className="container" style={{ padding: '4rem 0', color: 'var(--text-tertiary)' }}>Loading...</div>;
  if (!post) return <div className="container" style={{ padding: '4rem 0' }}>Post not found.</div>;

  const handleRemoveImage = async () => {
    await removePostImage(id);
    setCurrentImage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim() || submitting) return;
    setSubmitting(true);
    try {
      await updatePost(id, {
        title: title.trim(),
        excerpt: excerpt.trim() || '',
        content,
        readTime: readTime.trim() ? parseInt(readTime, 10) : 0,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      }, newImageFile || null);
      navigate('/admin');
    } catch (err) {
      console.error('Failed to update:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = { fontSize: '0.85rem', color: 'var(--text-tertiary)', display: 'block', marginBottom: '0.4rem' };

  return (
    <PageTransition>
      <div className="container" style={{ maxWidth: '900px', paddingTop: '3rem', paddingBottom: '4rem' }}>
        <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.8rem', marginBottom: '2rem', fontWeight: 400 }}>Edit Post</h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <label style={labelStyle}>Title</label>
            <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div>
            <label style={labelStyle}>Excerpt</label>
            <input className="form-input" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Tags (comma-separated)</label>
            <input className="form-input" value={tags} onChange={(e) => setTags(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Read time (minutes)</label>
            <input className="form-input" type="number" min="1" value={readTime} onChange={(e) => setReadTime(e.target.value)} placeholder="Auto-estimated if left empty" style={{ maxWidth: '220px' }} />
          </div>
          <div>
            <label style={labelStyle}>Image</label>
            {currentImage && !newImageFile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                <img src={currentImage} alt="Current" style={{ maxWidth: '200px', maxHeight: '120px', borderRadius: 'var(--radius-sm)', objectFit: 'cover' }} />
                <button type="button" className="btn btn-danger" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={handleRemoveImage}>Remove</button>
              </div>
            )}
            <input type="file" accept="image/*" style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }} onChange={(e) => setNewImageFile(e.target.files[0] || null)} />
            {newImageFile && <span style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginLeft: '0.5rem' }}>New image selected</span>}
          </div>
          <div>
            <label style={labelStyle}>Content</label>
            <RichEditor value={content} onChange={setContent} placeholder="Write your post..." />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
            <button type="button" className="btn" onClick={() => navigate('/admin')}>Cancel</button>
          </div>
        </form>
      </div>
    </PageTransition>
  );
}